/** "My escalations" queue — shows tasks where I'm a recipient or sender.
 *
 * Backed by GET /api/v1/escalations. Reviewers / admins can acknowledge
 * (PATCH status=acknowledged) or resolve (PATCH status=resolved + note)
 * each task. Tabs filter by status.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ExternalLink, Inbox, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { api, ApiError } from '@/lib/api';
import type { EscalationTask, EscalationStatus } from './types';

const STATUS_TABS: EscalationStatus[] = ['open', 'acknowledged', 'resolved'];
const STATUS_CLS: Record<EscalationStatus, string> = {
  open: 'bg-amber-50 text-amber-800 border-amber-200',
  acknowledged: 'bg-blue-50 text-blue-700 border-blue-200',
  resolved: 'bg-green-50 text-green-700 border-green-200',
};

function TaskCard({ t, onUpdated }: { t: EscalationTask; onUpdated: () => void }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function patch(status: 'acknowledged' | 'resolved') {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/v1/escalations/${t.id}`, {
        status,
        resolution_note: status === 'resolved' ? (note || undefined) : undefined,
      });
      onUpdated();
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Update failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardContent className="pt-3 pb-3 px-4 space-y-2">
        <div className="flex items-start gap-2 flex-wrap">
          <Badge variant="outline" className={`text-[10px] ${STATUS_CLS[t.status]}`}>
            {t.status}
          </Badge>
          <span className="text-xs text-muted-foreground">
            Stage: <span className="font-medium text-foreground">{t.stage}</span>
          </span>
          <span className="text-xs text-muted-foreground ml-auto">
            {new Date(t.created_at).toLocaleString()}
          </span>
        </div>
        <p className="text-sm whitespace-pre-wrap">{t.comment}</p>
        {t.agent_summary && (
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground">Agent summary</summary>
            <p className="mt-1 text-muted-foreground whitespace-pre-wrap border-l-2 pl-2">
              {t.agent_summary}
            </p>
          </details>
        )}
        {t.resolution_note && (
          <p className="text-xs italic text-muted-foreground border-l-2 pl-2">
            Resolution: {t.resolution_note}
          </p>
        )}
        <div className="flex items-center gap-2 pt-1">
          <Button variant="outline" size="sm" asChild className="gap-1.5">
            <Link to={`/jobs/${t.job_id}`}>
              <ExternalLink className="h-3 w-3" aria-hidden="true" />
              Open job
            </Link>
          </Button>
          {t.status === 'open' && (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => { void patch('acknowledged'); }}
            >
              Acknowledge
            </Button>
          )}
          {t.status !== 'resolved' && (
            <>
              <input
                type="text"
                placeholder="Resolution note (optional)"
                value={note}
                onChange={(e) => { setNote(e.target.value); }}
                className="flex-1 text-xs rounded border px-2 py-1 bg-background"
              />
              <Button
                size="sm"
                disabled={busy}
                onClick={() => { void patch('resolved'); }}
                className="gap-1.5"
              >
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                Resolve
              </Button>
            </>
          )}
        </div>
        {error && (
          <Alert variant="destructive">
            <AlertDescription className="text-xs">{error}</AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

export function EscalationsPage() {
  const [tab, setTab] = useState<EscalationStatus>('open');
  const [items, setItems] = useState<EscalationTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ data: EscalationTask[] }>(`/api/v1/escalations?status=${tab}`)
      .then((r) => { setItems(r.data); })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Failed to load escalations.');
      })
      .finally(() => { setLoading(false); });
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4 max-w-4xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Inbox className="h-6 w-6" aria-hidden="true" />
          My escalations
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Tasks routed to you by your team. Acknowledge to claim ownership; resolve once handled.
        </p>
      </div>
      <div className="flex items-center gap-2 border-b">
        {STATUS_TABS.map((s) => (
          <button
            key={s}
            onClick={() => { setTab(s); }}
            className={`px-3 py-1.5 text-sm capitalize border-b-2 transition-colors ${
              tab === s ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {s}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading…
        </div>
      ) : error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : items.length === 0 ? (
        <Card className="border-dashed bg-muted/30">
          <CardContent className="text-center py-12 text-sm text-muted-foreground">
            No {tab} escalations.
          </CardContent>
        </Card>
      ) : (
        <ul className="space-y-2">
          {items.map((t) => (
            <li key={t.id}>
              <TaskCard t={t} onUpdated={load} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
